import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button as MuiButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { Mail, Pencil, User, X } from 'lucide-react';
import * as Yup from 'yup';
import {
  PartnerEntity,
  PartnerUser,
  UserGroup,
  UserRole,
} from '../../../../../utils/types/admin_types';
import { FormattedSelectOptions } from '../../../../../utils/types/analytics/analytics_types';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { getFormattedEntityList } from './AddPartnerEmployeeModal';

export type EditEmployeeModalProps = {
  open: boolean;
  onClose: () => void;
  partnerEntityList: PartnerEntity[];
  partnerEmployee: PartnerUser | null | undefined;
  userGroupList: UserGroup[];
  userRoleList: UserRole[];
  editPartnerEmployee: (
    id: string,
    partnerEntityId: FormattedSelectOptions,
    jobTitle: string,
    roles: SelectOptions[],
    groups: SelectOptions[],
  ) => void;
};

const findPartnerEntityById = (
  entities: PartnerEntity[],
  targetId: string | null | undefined,
  currentLevel: number,
): { entity: PartnerEntity; level: number } | undefined => {
  if (!targetId) return undefined;
  for (const entity of entities) {
    if (entity.id === targetId) {
      return { entity, level: currentLevel };
    }
    const nestedResult = findPartnerEntityById(
      entity.subEntities,
      targetId,
      currentLevel + 1,
    );
    if (nestedResult) {
      return nestedResult;
    }
  }
  return undefined;
};

const validationSchema = Yup.object({
  partnerEntity: Yup.object()
    .nullable()
    .required('Partner entity organization is required'),
  jobTitle: Yup.string().trim().required('Job title is required'),
});

export const EditPartnerEmployeeModal: React.FC<EditEmployeeModalProps> = ({
  open,
  onClose,
  partnerEntityList,
  partnerEmployee,
  userGroupList,
  userRoleList,
  editPartnerEmployee,
}) => {
  const formattedOrgList = useMemo(
    () => getFormattedEntityList(partnerEntityList),
    [partnerEntityList],
  );

  const groupOptions: SelectOptions[] = useMemo(
    () =>
      userGroupList.map((group) => ({
        value: group.id,
        label: group.groupName,
      })),
    [userGroupList],
  );

  const roleOptions: SelectOptions[] = useMemo(
    () =>
      userRoleList.map((role) => ({
        value: role.id,
        label: role.roleName,
      })),
    [userRoleList],
  );

  const initialEntity = useMemo(() => {
    if (!partnerEmployee) return null;
    const found = findPartnerEntityById(
      partnerEntityList,
      partnerEmployee.partnerEntityId,
      0,
    );
    return found
      ? {
          value: found.entity.id,
          label: found.entity.name,
          level: found.level,
        }
      : null;
  }, [partnerEntityList, partnerEmployee]);

  const initialGroups: SelectOptions[] = useMemo(
    () =>
      partnerEmployee?.groups?.map((group) => ({
        value: group.id,
        label: group.groupName,
      })) || [],
    [partnerEmployee],
  );

  const initialRoles: SelectOptions[] = useMemo(
    () =>
      partnerEmployee?.roles?.map((role) => ({
        value: role.id,
        label: role.roleName,
      })) || [],
    [partnerEmployee],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      partnerEntity: initialEntity as FormattedSelectOptions | null,
      jobTitle: partnerEmployee?.jobTitle || '',
      groups: initialGroups,
      roles: initialRoles,
    },
    validationSchema,
    onSubmit: (values) => {
      if (partnerEmployee && values.partnerEntity) {
        editPartnerEmployee(
          partnerEmployee.id,
          values.partnerEntity,
          values.jobTitle.trim(),
          values.roles,
          values.groups,
        );
        onClose();
      }
    },
  });

  if (!partnerEmployee) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.18)',
          overflow: 'hidden',
        },
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle
          sx={{
            p: 2.5,
            pb: 2,
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: '#eff6ff',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Pencil size={18} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  color: '#0f172a',
                  lineHeight: 1.2,
                }}
              >
                Edit Employee Details
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Update organization assignment, job title, roles, and groups
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: '#94a3b8',
              '&:hover': { bgcolor: '#f8fafc', color: '#0f172a' },
            }}
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 2.5,
            pt: '20px !important',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.2,
          }}
        >
          {/* Employee User Summary Card */}
          <Box
            sx={{
              p: 2,
              borderRadius: '10px',
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.8,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <User size={16} color="#64748b" />
              <Typography
                sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0f172a' }}
              >
                {partnerEmployee.user.firstName} {partnerEmployee.user.lastName}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Mail size={15} color="#64748b" />
              <Typography sx={{ fontSize: '0.8125rem', color: '#64748b' }}>
                {partnerEmployee.user.email}
              </Typography>
            </Box>
          </Box>

          <Autocomplete
            options={formattedOrgList}
            getOptionLabel={(option) => {
              if (option.level && option.level > 0) {
                return `${'— '.repeat(option.level)}${option.label}`;
              }
              return option.label;
            }}
            isOptionEqualToValue={(option, value) =>
              option.value === value?.value
            }
            value={formik.values.partnerEntity}
            onChange={(_, newValue) => {
              formik.setFieldValue('partnerEntity', newValue);
            }}
            onBlur={() => formik.setFieldTouched('partnerEntity', true)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Select Partner Entity"
                placeholder="Choose partner organization..."
                error={
                  formik.touched.partnerEntity &&
                  Boolean(formik.errors.partnerEntity)
                }
                helperText={
                  formik.touched.partnerEntity &&
                  (formik.errors.partnerEntity as string)
                }
              />
            )}
          />

          <TextField
            fullWidth
            size="small"
            name="jobTitle"
            label="Job Title"
            placeholder="e.g. Operations Specialist"
            value={formik.values.jobTitle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.jobTitle && Boolean(formik.errors.jobTitle)}
            helperText={formik.touched.jobTitle && formik.errors.jobTitle}
          />

          <Autocomplete
            multiple
            options={groupOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            value={formik.values.groups}
            onChange={(_, newValue) => {
              formik.setFieldValue('groups', newValue);
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.label}
                  size="small"
                  sx={{
                    bgcolor: '#f1f5f9',
                    color: '#334155',
                    fontWeight: 500,
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Group Membership"
                placeholder="Select groups..."
              />
            )}
          />

          <Autocomplete
            multiple
            options={roleOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            value={formik.values.roles}
            onChange={(_, newValue) => {
              formik.setFieldValue('roles', newValue);
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.label}
                  size="small"
                  sx={{
                    bgcolor: '#eff6ff',
                    color: '#2563eb',
                    fontWeight: 500,
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Role Membership"
                placeholder="Select roles..."
              />
            )}
          />
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.5,
            pt: 1.5,
            borderTop: '1px solid #f1f5f9',
            gap: 1,
          }}
        >
          <MuiButton
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 2,
              color: '#475569',
              borderColor: '#cbd5e1',
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
            }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<Pencil size={15} />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 2.5,
              bgcolor: '#2563eb',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
              '&:hover': {
                bgcolor: '#1d4ed8',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
              },
            }}
          >
            Save Changes
          </MuiButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
